/**
 * ContratosPage — contratos do cliente, só leitura (B1.7 §4 — Gestor).
 *
 * FASE-C: cabeçalho canônico em todos os estados, cartão de contrato com
 * hierarquia (tipo como micro-rótulo, número como identificador em SF Mono,
 * datas em linha própria que quebra bem no 375px) e mensagens humanas. Sem
 * ações — renovação e cancelamento seguem por conversa direta com a IGM.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, Badge, EmptyState } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { PortalPage, PortalCarregando } from '@/components/cliente/PortalPage';
import { useCliente } from '@/contexts/ClienteContext';
import {
  useClienteDashboard,
  useContratos,
  type ContratoListItem,
} from '@/lib/cliente-portal-store';
import { mensagemErro } from '@/lib/mensagem-publica';

import { formatarData } from '@/lib/format-date';
export function ContratosPage() {
  const { clienteAtivo } = useCliente();
  const navigate = useNavigate();
  const toast = useToast();
  const dashQuery = useClienteDashboard();
  const podeVer = dashQuery.data?.permissoes.ver_contratos ?? false;

  useEffect(() => {
    if (dashQuery.isSuccess && !podeVer) {
      toast.show('Você não tem acesso aos contratos.', { variant: 'info' });
      navigate('/cliente/dashboard', { replace: true });
    }
  }, [dashQuery.isSuccess, podeVer, navigate, toast]);

  const { data, isLoading, isError, error } = useContratos();

  if (!podeVer && dashQuery.isSuccess) return null;

  return (
    <PortalPage
      testId="contratos-page"
      title="Contratos"
      subtitle={
        clienteAtivo
          ? `Os contratos de ${clienteAtivo.nome}, ativos e encerrados. Para renovar ou cancelar, fale direto com a IGM.`
          : 'Contratos ativos e encerrados.'
      }
    >
      {!clienteAtivo || dashQuery.isLoading || isLoading ? (
        <PortalCarregando rotulo="Carregando seus contratos…" />
      ) : isError ? (
        <Alert variant="danger">
          {mensagemErro(error, 'Não conseguimos carregar seus contratos agora. Tente atualizar a página.')}
        </Alert>
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="Nenhum contrato por aqui ainda"
          description="Assim que a IGM registrar um contrato para este condomínio, ele aparece nesta tela com prazo e situação."
        />
      ) : (
        <ul className="space-y-2.5" data-testid="lista-contratos">
          {data.items.map((c) => (
            <ContratoCard key={c.id} contrato={c} />
          ))}
        </ul>
      )}
    </PortalPage>
  );
}

function ContratoCard({ contrato: c }: { contrato: ContratoListItem }) {
  return (
    <li
      className="rounded-xl border border-line bg-paper-surface p-4 dark:border-line-dark dark:bg-paper-dark-surface"
      data-testid={`contrato-card-${c.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="block text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
            {labelTipo(c.tipo)}
          </span>
          <span className="mt-0.5 block font-medium text-ink-1 dark:text-ink-dark-1">
            {c.numero ? (
              <>
                Contrato <span className="font-mono tabular-nums">{c.numero}</span>
              </>
            ) : (
              'Contrato'
            )}
          </span>
        </div>
        <Badge variant={statusVariant(c.status)} size="sm" className="shrink-0">
          {labelStatus(c.status)}
        </Badge>
      </div>
      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-line pt-3 text-xs dark:border-line-dark">
        <Periodo rotulo="Início" valor={formatIsoShort(c.inicio_iso)} />
        {c.fim_previsto_iso && (
          <Periodo rotulo="Fim previsto" valor={formatIsoShort(c.fim_previsto_iso)} />
        )}
        {c.fim_real_iso && <Periodo rotulo="Encerrado em" valor={formatIsoShort(c.fim_real_iso)} />}
      </dl>
    </li>
  );
}

function Periodo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-ink-3 dark:text-ink-dark-3">{rotulo}</dt>
      <dd className="font-mono tabular-nums text-ink-1 dark:text-ink-dark-1">{valor}</dd>
    </div>
  );
}

/**
 * O banco guarda `tipo` como código ('recorrente' | 'projeto'). Código de banco
 * não é rótulo de tela — o cliente lê o nome do que ele contratou.
 */
function labelTipo(tipo: string): string {
  const map: Record<string, string> = {
    recorrente: 'Serviço mensal',
    projeto: 'Projeto',
  };
  return map[tipo] ?? tipo;
}

function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'ativo') return 'success';
  if (status === 'pausado') return 'warning';
  if (status === 'encerrado' || status === 'cancelado') return 'neutral';
  return 'neutral';
}

function labelStatus(status: string): string {
  const map: Record<string, string> = {
    ativo: 'Ativo',
    pausado: 'Pausado',
    encerrado: 'Encerrado',
    cancelado: 'Cancelado',
  };
  return map[status] ?? status;
}

function formatIsoShort(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
